import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Zap, BarChart3, Phone, Database } from "lucide-react";

const addonIcons = {
  ai_priority: Zap,
  analytics: BarChart3,
  multi_number: Phone,
  crm_integration: Database,
};

export const SubscriptionAddons = () => {
  const queryClient = useQueryClient();

  const { data: subscription } = useQuery({
    queryKey: ['current-subscription'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('organization_id', user.id)
        .eq('status', 'active')
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    }
  });

  const { data: availableAddons } = useQuery({
    queryKey: ['available-addons'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_addons')
        .select('*')
        .eq('is_active', true);
      
      if (error) throw error;
      return data;
    }
  });

  const { data: activeAddons } = useQuery({
    queryKey: ['active-addons', subscription?.id],
    queryFn: async () => {
      if (!subscription?.id) return [];

      const { data, error } = await supabase
        .from('subscription_addon_assignments')
        .select('addon_id')
        .eq('subscription_id', subscription.id);
      
      if (error) throw error;
      return data.map(a => a.addon_id);
    },
    enabled: !!subscription?.id
  });

  const toggleAddonMutation = useMutation({
    mutationFn: async ({ addonId, isActive }: { addonId: string; isActive: boolean }) => {
      if (!subscription?.id) throw new Error('No active subscription');

      if (isActive) {
        const { error } = await supabase
          .from('subscription_addon_assignments')
          .delete()
          .eq('subscription_id', subscription.id)
          .eq('addon_id', addonId);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('subscription_addon_assignments')
          .insert({
            subscription_id: subscription.id,
            addon_id: addonId
          });
        
        if (error) throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['active-addons'] });
      toast.success(variables.isActive ? 'Add-on removed' : 'Add-on activated');
    },
    onError: (error) => {
      toast.error(`Failed to update add-on: ${error.message}`);
    }
  });

  if (!subscription) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Add-ons</CardTitle>
          <CardDescription>Subscribe to a plan to add features</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-2xl font-bold mb-2">Enhance Your Plan</h3>
        <p className="text-muted-foreground">Add powerful features to your subscription</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {availableAddons?.map((addon) => {
          const isActive = activeAddons?.includes(addon.id);
          const Icon = addonIcons[addon.addon_type as keyof typeof addonIcons] || Zap;

          return (
            <Card key={addon.id} className={isActive ? "border-primary" : ""}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{addon.name}</CardTitle>
                      <CardDescription>{addon.description}</CardDescription>
                    </div>
                  </div>
                  {isActive && <Badge>Active</Badge>}
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="text-3xl font-bold">
                  ${Number(addon.monthly_price).toFixed(2)}
                  <span className="text-lg font-normal text-muted-foreground">/mo</span>
                </div>
              </CardContent>

              <CardFooter>
                <Button
                  className="w-full"
                  variant={isActive ? "outline" : "default"}
                  onClick={() => toggleAddonMutation.mutate({ addonId: addon.id, isActive })}
                  disabled={toggleAddonMutation.isPending}
                >
                  {isActive ? 'Remove Add-on' : 'Add to Plan'}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
